/**
 * CDB54 Calendrier+ - Vue Router Configuration
 * Defines application routes with authentication guards
 */

import { createRouter, createWebHistory } from 'vue-router';
import type { RouteRecordRaw, NavigationGuardNext, RouteLocationNormalized } from 'vue-router';
import { useAuthStore } from '@/stores/auth';

const routes: RouteRecordRaw[] = [
  {
    path: '/',
    redirect: '/calendar',
  },
  {
    path: '/home',
    name: 'home',
    component: () => import('@/views/Home.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/login',
    name: 'login',
    component: () => import('@/views/Login.vue'),
    meta: { guest: true },
  },
  {
    path: '/faq',
    name: 'faq',
    component: () => import('@/views/Faq.vue'),
    meta: { public: true },
  },
  {
    path: '/calendar',
    name: 'calendar',
    component: () => import('@/views/Calendar.vue'),
    meta: { public: true },
  },
  {
    path: '/calendar/event/:id',
    name: 'event-detail',
    component: () => import('@/views/EventDetail.vue'),
    meta: { public: true },
  },
  {
    path: '/profile',
    name: 'profile',
    component: () => import('@/views/Profile.vue'),
    meta: { requiresAuth: true },
  },
  // Admin routes (Story 3.1-3.8)
  {
    path: '/admin/events',
    name: 'admin-events',
    component: () => import('@/views/admin/AdminEvents.vue'),
    meta: { requiresAuth: true, requiresPermission: 'events.view' },
  },
  {
    path: '/admin/events/create',
    name: 'admin-event-create',
    component: () => import('@/views/admin/AdminEventForm.vue'),
    meta: { requiresAuth: true, requiresPermission: 'events.create' },
  },
  {
    path: '/admin/events/:id/edit',
    name: 'admin-event-edit',
    component: () => import('@/views/admin/AdminEventForm.vue'),
    meta: { requiresAuth: true, requiresPermission: 'events.edit' },
  },
  // GDPR route (Story 11.1-11.2)
  {
    path: '/gdpr',
    name: 'gdpr',
    component: () => import('@/views/GdprPage.vue'),
    meta: { public: true },
  },
];

const router = createRouter({
  history: createWebHistory('/'),
  routes,
});

/**
 * Navigation guard for authentication
 */
router.beforeEach(async (
  to: RouteLocationNormalized,
  _from: RouteLocationNormalized,
  next: NavigationGuardNext
) => {
  const authStore = useAuthStore();

  // Skip auth check for public routes
  if (to.meta.public) {
    next();
    return;
  }

  // Check auth status if not already checked
  if (!authStore.isAuthenticated) {
    await authStore.checkAuth();
  }

  // Route requires authentication
  if (to.meta.requiresAuth && !authStore.isAuthenticated) {
    next({ name: 'login', query: { redirect: to.fullPath } });
    return;
  }

  // Route is for guests only (login page)
  if (to.meta.guest && authStore.isAuthenticated) {
    next({ name: 'home' });
    return;
  }

  // Route requires specific permission (Story 3.1)
  if (to.meta.requiresPermission && authStore.isAuthenticated) {
    const permission = to.meta.requiresPermission as string;
    if (!authStore.hasPermission(permission)) {
      next({ name: 'home' });
      return;
    }
  }

  next();
});

export default router;
